/**
 * In-account service ordering (AM API). Lets a logged-in customer buy a new hosting
 * plan / server: browse the catalog, submit an order (card pre-auth up front), poll
 * provisioning, and retry after a decline. See docs/amapi-service-add.md.
 *
 * All calls go through amapi (form-encoded POST, authed by the session). The AM API
 * envelope is `{ result: 'success' | 'error', resultmsg, ... }`; we normalize a
 * failure (or a missing/!success body) into a thrown Error carrying resultmsg.
 */
import amapi from '@/api/amapi'

async function call (method, params = {}) {
  let res
  try {
    res = await amapi.makecall(method, params)
  } catch (e) {
    // makecall throws the resultmsg string on result:'error'.
    throw new Error(typeof e === 'string' ? e : (e && e.message) || 'The request failed.')
  }
  if (!res) throw new Error('No response from the billing system. Please try again.')
  if (res.result && res.result !== 'success') {
    throw new Error(res.resultmsg || res.msg || 'The request could not be completed.')
  }
  return res
}

export default {
  /**
   * List orderable plans for a group (e.g. shared|reseller|vps|dedicated|kvm). An
   * invalid coupon still returns the catalog (at standard pricing) with
   * `couponError` set.
   * @returns {Promise<{products: Array, couponError: string|null}>}
   */
  async catalog ({ group, coupon } = {}) {
    const res = await call('service_catalog', {
      ...(group ? { group } : {}),
      ...(coupon ? { coupon } : {}),
    })
    return { products: res.products || [], couponError: res.coupon_error || null }
  },

  /**
   * Submit an order. Pre-authorizes the first-term price on the card up front; only
   * creates the order if that succeeds. Returns the orderid to poll.
   * @param {Object} p { product_id, billing_term, cardcvv, domain?, coupon?, cardnum?, expdate? }
   * @returns {Promise<number>} orderid
   */
  async add (p) {
    const res = await call('service_add', p)
    return res.orderid
  },

  /** Poll provisioning. @returns {Promise<{status, reason}>} status: processing|active|declined|needs_review */
  async status (orderid) {
    const res = await call('service_add_status', { orderid })
    return { status: res.status, reason: res.reason || null }
  },

  /** Re-queue a declined order after the saved card was updated. */
  async retry (orderid) {
    await call('service_add_retry', { orderid })
  },
}
